/**
 * Airtable Import Sheet
 * Multi-step wizard for importing fields from an Airtable base
 * 
 * Steps:
 * 1. Enter Airtable credentials (Base ID + Personal Access Token)
 * 2. Select table from the base
 * 3. Select fields to import (multi-select with preview)
 * 4. Preview and edit field configurations before creation
 */

import { useState, useMemo } from 'react';
import {
    ArrowLeft,
    ArrowRight,
    Check,
    Loader2,
    Database,
    Table,
    Columns,
    Eye,
    Edit2,
    Trash2,
    CheckSquare,
    Square,
    ExternalLink,
    RefreshCw,
    AlertCircle,
    Plus,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Table as TableComponent,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useCreateField } from '@/hooks/useFieldsQuery';
import {
    fetchAirtableTables,
    prepareFieldForImport,
    getAirtableTypeLabel,
    toSnakeCase,
    type AirtableTable,
    type ImportFieldConfig,
} from '@/lib/airtable';

// Available field types for editing
const FIELD_TYPES = [
    { value: 'string', label: 'String' },
    { value: 'text', label: 'Text' },
    { value: 'boolean', label: 'Boolean' },
    { value: 'integer', label: 'Integer' },
    { value: 'float', label: 'Float' },
    { value: 'decimal', label: 'Decimal' },
    { value: 'timestamp', label: 'Timestamp' },
    { value: 'date', label: 'Date' },
    { value: 'time', label: 'Time' },
    { value: 'json', label: 'JSON' },
    { value: 'uuid', label: 'UUID' },
];

// Available interfaces for editing
const INTERFACES = [
    { value: 'input', label: 'Input' },
    { value: 'input-multiline', label: 'Textarea' },
    { value: 'input-rich-text-md', label: 'Markdown' },
    { value: 'select-dropdown', label: 'Dropdown' },
    { value: 'select-multiple-dropdown', label: 'Multi-Select' },
    { value: 'boolean', label: 'Checkbox' },
    { value: 'datetime', label: 'DateTime' },
    { value: 'select-radio', label: 'Radio Buttons' },
];

type Step = 'credentials' | 'table' | 'fields' | 'preview';

interface AirtableImportSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    collection: string;
    onSuccess?: () => void;
}

export function AirtableImportSheet({
    open,
    onOpenChange,
    collection,
    onSuccess,
}: AirtableImportSheetProps) {
    // Current step
    const [step, setStep] = useState<Step>('credentials');

    // Step 1: Credentials
    const [baseId, setBaseId] = useState('');
    const [apiToken, setApiToken] = useState('');
    const [isLoadingTables, setIsLoadingTables] = useState(false);
    const [credentialsError, setCredentialsError] = useState<string | null>(null);

    // Step 2: Table selection
    const [tables, setTables] = useState<AirtableTable[]>([]);
    const [selectedTableId, setSelectedTableId] = useState<string>('');

    // Step 3 & 4: Field selection and preview
    const [fields, setFields] = useState<ImportFieldConfig[]>([]);
    const [editingField, setEditingField] = useState<string | null>(null);

    // Import state
    const [isImporting, setIsImporting] = useState(false);
    const createField = useCreateField(collection);

    // Get selected table
    const selectedTable = useMemo(() => {
        return tables.find(t => t.id === selectedTableId);
    }, [tables, selectedTableId]);

    // Get selected fields
    const selectedFields = useMemo(() => {
        return fields.filter(f => f.selected);
    }, [fields]);

    // Reset the form
    const resetForm = () => {
        setStep('credentials');
        setBaseId('');
        setApiToken('');
        setTables([]);
        setSelectedTableId('');
        setFields([]);
        setCredentialsError(null);
        setEditingField(null);
        setIsLoadingTables(false);
        setIsImporting(false);
    };

    const handleClose = () => {
        resetForm();
        onOpenChange(false);
    };

    // Step 1: Fetch tables from Airtable
    const handleFetchTables = async () => {
        if (!baseId.trim() || !apiToken.trim()) {
            setCredentialsError('Please enter both Base ID and API Token');
            return;
        }

        setIsLoadingTables(true);
        setCredentialsError(null);

        try {
            const fetchedTables = await fetchAirtableTables(baseId.trim(), apiToken.trim());
            setTables(fetchedTables);
            setStep('table');
        } catch (error) {
            setCredentialsError(error instanceof Error ? error.message : 'Failed to fetch tables');
        } finally {
            setIsLoadingTables(false);
        }
    };

    // Step 2: Select table and load fields
    const handleSelectTable = (tableId: string) => {
        setSelectedTableId(tableId);
        const table = tables.find(t => t.id === tableId);
        if (table) {
            const preparedFields = table.fields
                //.filter(f => f.id !== table.primaryFieldId)
                .map(f => prepareFieldForImport(f));
            setFields(preparedFields);
        }
    };

    const handleContinueToFields = () => {
        if (selectedTableId && fields.length > 0) {
            setStep('fields');
        }
    };

    // Step 3: Toggle field selection
    const toggleFieldSelection = (fieldId: string) => {
        setFields(prev =>
            prev.map(f =>
                f.airtableId === fieldId ? { ...f, selected: !f.selected } : f
            )
        );
    };

    const selectAllFields = () => {
        setFields(prev => prev.map(f => ({ ...f, selected: true })));
    };

    const deselectAllFields = () => {
        setFields(prev => prev.map(f => ({ ...f, selected: false })));
    };

    const handleContinueToPreview = () => {
        if (selectedFields.length > 0) {
            setStep('preview');
        }
    };

    // Step 4: Edit field configuration
    const updateField = (fieldId: string, updates: Partial<ImportFieldConfig>) => {
        setFields(prev =>
            prev.map(f =>
                f.airtableId === fieldId ? { ...f, ...updates } : f
            )
        );
    };

    const removeField = (fieldId: string) => {
        setFields(prev =>
            prev.map(f =>
                f.airtableId === fieldId ? { ...f, selected: false } : f
            )
        );
    };

    // Update choices for a field
    const updateChoices = (fieldId: string, choices: { text: string; value: string }[]) => {
        const field = fields.find(f => f.airtableId === fieldId);
        if (!field) return;

        const newOptions = {
            ...field.options,
            choices: choices.length > 0 ? choices : undefined,
        };

        // Remove options object if it's empty
        const finalOptions = Object.keys(newOptions).length > 0 && newOptions.choices
            ? newOptions
            : undefined;

        updateField(fieldId, { options: finalOptions });
    };

    // Add a new choice to a field
    const addChoice = (fieldId: string) => {
        const field = fields.find(f => f.airtableId === fieldId);
        if (!field) return;

        const currentChoices = (field.options?.choices as { text: string; value: string }[] | undefined) || [];
        updateChoices(fieldId, [...currentChoices, { text: '', value: '' }]);
    };

    // Remove a choice from a field
    const removeChoice = (fieldId: string, choiceIndex: number) => {
        const field = fields.find(f => f.airtableId === fieldId);
        if (!field) return;

        const currentChoices = (field.options?.choices as { text: string; value: string }[] | undefined) || [];
        const newChoices = currentChoices.filter((_, idx) => idx !== choiceIndex);
        updateChoices(fieldId, newChoices);
    };

    // Update a specific choice
    const updateChoice = (
        fieldId: string,
        choiceIndex: number,
        field: 'text' | 'value',
        newValue: string
    ) => {
        const fieldConfig = fields.find(f => f.airtableId === fieldId);
        if (!fieldConfig) return;

        const currentChoices = (fieldConfig.options?.choices as { text: string; value: string }[] | undefined) || [];
        const newChoices = [...currentChoices];
        if (newChoices[choiceIndex]) {
            newChoices[choiceIndex] = {
                ...newChoices[choiceIndex],
                [field]: newValue,
            };
            updateChoices(fieldId, newChoices);
        }
    };

    // Import fields
    const handleImport = async () => {
        if (selectedFields.length === 0) return;

        setIsImporting(true);
        let successCount = 0;
        let errorCount = 0;

        for (const fieldConfig of selectedFields) {
            try {
                await createField.mutateAsync({
                    field: fieldConfig.field,
                    type: fieldConfig.type,
                    meta: {
                        interface: fieldConfig.interface,
                        options: fieldConfig.options,
                        special: fieldConfig.special,
                        note: fieldConfig.description,
                    },
                    schema: fieldConfig.type !== 'alias' ? {
                        is_nullable: true,
                    } : undefined,
                });
                successCount++;
            } catch (error) {
                console.error(`Failed to create field ${fieldConfig.field}:`, error);
                errorCount++;
            }
        }

        setIsImporting(false);

        if (errorCount === 0) {
            toast.success(`Successfully imported ${successCount} field${successCount !== 1 ? 's' : ''}`);
            handleClose();
            onSuccess?.();
        } else if (successCount > 0) {
            toast.warning(`Imported ${successCount} field${successCount !== 1 ? 's' : ''}, ${errorCount} failed`);
            onSuccess?.();
        } else {
            toast.error('Failed to import fields');
        }
    };

    // Navigation
    const handleBack = () => {
        switch (step) {
            case 'table':
                setStep('credentials');
                break;
            case 'fields':
                setStep('table');
                break;
            case 'preview':
                setStep('fields');
                break;
        }
    };

    // Validate field name
    const isValidFieldName = (name: string): boolean => {
        return /^[a-z_][a-z0-9_]*$/.test(name);
    };

    // Check for duplicate field names
    const hasDuplicateNames = useMemo(() => {
        const names = selectedFields.map(f => f.field);
        return names.length !== new Set(names).size;
    }, [selectedFields]);

    // Check if all selected fields have valid names
    const allNamesValid = useMemo(() => {
        return selectedFields.every(f => isValidFieldName(f.field));
    }, [selectedFields]);

    return (
        <Sheet open={open} onOpenChange={handleClose}>
            <SheetContent className="w-[800px] sm:max-w-[800px] flex flex-col h-full p-0">
                <SheetHeader className="p-6 pb-0">
                    <SheetTitle className="flex items-center gap-2">
                        <Database className="h-5 w-5" />
                        Import Fields from Airtable
                    </SheetTitle>
                    <SheetDescription>
                        {step === 'credentials' && 'Enter your Airtable credentials to get started'}
                        {step === 'table' && 'Select the table you want to import fields from'}
                        {step === 'fields' && 'Choose which fields to import'}
                        {step === 'preview' && 'Review and customize fields before importing'}
                    </SheetDescription>
                </SheetHeader>

                {/* Progress indicator */}
                <div className="flex items-center justify-center gap-2 px-6 py-4">
                    {(['credentials', 'table', 'fields', 'preview'] as Step[]).map((s, index) => (
                        <div key={s} className="flex items-center">
                            <div
                                className={cn(
                                    'h-8 w-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors',
                                    step === s
                                        ? 'bg-primary text-primary-foreground'
                                        : (['credentials', 'table', 'fields', 'preview'].indexOf(step) > index)
                                            ? 'bg-primary/20 text-primary'
                                            : 'bg-muted text-muted-foreground'
                                )}
                            >
                                {index + 1}
                            </div>
                            {index < 3 && (
                                <div
                                    className={cn(
                                        'w-12 h-0.5 mx-2',
                                        (['credentials', 'table', 'fields', 'preview'].indexOf(step) > index)
                                            ? 'bg-primary/50'
                                            : 'bg-muted'
                                    )}
                                />
                            )}
                        </div>
                    ))}
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-4">
                    {/* Step 1: Credentials */}
                    {step === 'credentials' && (
                        <div className="space-y-6">
                            <Alert>
                                <AlertCircle className="h-4 w-4" />
                                <AlertDescription>
                                    You'll need an Airtable Personal Access Token with{' '}
                                    <code className="text-xs bg-muted px-1 py-0.5 rounded">schema.bases:read</code>{' '}
                                    scope.{' '}
                                    <a
                                        href="https://airtable.com/create/tokens"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-primary hover:underline inline-flex items-center gap-1"
                                    >
                                        Create one here <ExternalLink className="h-3 w-3" />
                                    </a>
                                </AlertDescription>
                            </Alert>

                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="baseId">Base ID</Label>
                                    <Input
                                        id="baseId"
                                        value={baseId}
                                        onChange={(e) => setBaseId(e.target.value)}
                                        placeholder="appXXXXXXXXXXXXXX"
                                        className="font-mono"
                                    />
                                    <p className="text-xs text-muted-foreground">
                                        Find this in your Airtable base URL: airtable.com/<span className="font-mono text-primary">appXXX</span>/...
                                    </p>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="apiToken">Personal Access Token</Label>
                                    <Input
                                        id="apiToken"
                                        type="password"
                                        value={apiToken}
                                        onChange={(e) => setApiToken(e.target.value)}
                                        placeholder="patXXXXXXXXXXXXXX.XXXXXXXX"
                                        className="font-mono"
                                    />
                                    <p className="text-xs text-muted-foreground">
                                        Your personal access token starting with 'pat'
                                    </p>
                                </div>
                            </div>

                            {credentialsError && (
                                <Alert variant="destructive">
                                    <AlertCircle className="h-4 w-4" />
                                    <AlertDescription>{credentialsError}</AlertDescription>
                                </Alert>
                            )}
                        </div>
                    )}

                    {/* Step 2: Table Selection */}
                    {step === 'table' && (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <p className="text-sm text-muted-foreground">
                                    Found {tables.length} table{tables.length !== 1 ? 's' : ''} in this base
                                </p>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={handleFetchTables}
                                    disabled={isLoadingTables}
                                >
                                    <RefreshCw className={cn('h-4 w-4 mr-1', isLoadingTables && 'animate-spin')} />
                                    Refresh
                                </Button>
                            </div>

                            <div className="space-y-2">
                                {tables.map((table) => (
                                    <button
                                        key={table.id}
                                        onClick={() => handleSelectTable(table.id)}
                                        className={cn(
                                            'w-full flex items-center gap-3 p-4 rounded-lg border transition-colors text-left',
                                            selectedTableId === table.id
                                                ? 'border-primary bg-primary/5'
                                                : 'border-border hover:border-primary/50 hover:bg-accent'
                                        )}
                                    >
                                        <Table className="h-5 w-5 text-muted-foreground" />
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium truncate">{table.name}</p>
                                            <p className="text-xs text-muted-foreground">
                                                {table.fields.length} field{table.fields.length !== 1 ? 's' : ''}
                                            </p>
                                        </div>
                                        {selectedTableId === table.id && (
                                            <Check className="h-5 w-5 text-primary" />
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Step 3: Field Selection */}
                    {step === 'fields' && selectedTable && (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="font-medium">{selectedTable.name}</p>
                                    <p className="text-sm text-muted-foreground">
                                        {selectedFields.length} of {fields.length} field{fields.length !== 1 ? 's' : ''} selected
                                    </p>
                                </div>
                                <div className="flex gap-2">
                                    <Button variant="outline" size="sm" onClick={selectAllFields}>
                                        <CheckSquare className="h-4 w-4 mr-1" />
                                        Select All
                                    </Button>
                                    <Button variant="outline" size="sm" onClick={deselectAllFields}>
                                        <Square className="h-4 w-4 mr-1" />
                                        Deselect All
                                    </Button>
                                </div>
                            </div>

                            <ScrollArea className="h-[400px] rounded-lg border">
                                <TableComponent>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="w-12"></TableHead>
                                            <TableHead>Airtable Field</TableHead>
                                            <TableHead>Type</TableHead>
                                            <TableHead>Mapped Name</TableHead>
                                            <TableHead>Mapped Type</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {fields.map((field) => (
                                            <TableRow
                                                key={field.airtableId}
                                                className={cn(
                                                    'cursor-pointer',
                                                    field.selected && 'bg-primary/5'
                                                )}
                                                onClick={() => toggleFieldSelection(field.airtableId)}
                                            >
                                                <TableCell>
                                                    <Checkbox
                                                        checked={field.selected}
                                                        onCheckedChange={() => toggleFieldSelection(field.airtableId)}
                                                    />
                                                </TableCell>
                                                <TableCell className="font-medium">{field.airtableName}</TableCell>
                                                <TableCell>
                                                    <Badge variant="outline">
                                                        {getAirtableTypeLabel(field.airtableType)}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell className="font-mono text-sm text-muted-foreground">
                                                    {field.field}
                                                </TableCell>
                                                <TableCell>
                                                    <Badge variant="secondary">
                                                        {field.type}
                                                    </Badge>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </TableComponent>
                            </ScrollArea>
                        </div>
                    )}

                    {/* Step 4: Preview & Edit */}
                    {step === 'preview' && (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <p className="text-sm text-muted-foreground">
                                    Review and edit {selectedFields.length} field{selectedFields.length !== 1 ? 's' : ''} before importing
                                </p>
                            </div>

                            {hasDuplicateNames && (
                                <Alert variant="destructive">
                                    <AlertCircle className="h-4 w-4" />
                                    <AlertDescription>
                                        There are duplicate field names. Please rename them before importing.
                                    </AlertDescription>
                                </Alert>
                            )}

                            <ScrollArea className="h-[400px]">
                                <div className="space-y-3">
                                    {selectedFields.map((field) => {
                                        const isEditing = editingField === field.airtableId;
                                        const nameValid = isValidFieldName(field.field);

                                        return (
                                            <div
                                                key={field.airtableId}
                                                className={cn(
                                                    'rounded-lg border p-4 space-y-3',
                                                    !nameValid && 'border-destructive'
                                                )}
                                            >
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-2">
                                                        <Columns className="h-4 w-4 text-muted-foreground" />
                                                        <span className="text-sm text-muted-foreground">
                                                            {field.airtableName}
                                                        </span>
                                                        <Badge variant="outline" className="text-xs">
                                                            {getAirtableTypeLabel(field.airtableType)}
                                                        </Badge>
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => setEditingField(isEditing ? null : field.airtableId)}
                                                        >
                                                            {isEditing ? (
                                                                <Eye className="h-4 w-4" />
                                                            ) : (
                                                                <Edit2 className="h-4 w-4" />
                                                            )}
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => removeField(field.airtableId)}
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                </div>

                                                {isEditing ? (
                                                    <div className="space-y-4">
                                                        <div className="grid grid-cols-3 gap-4">
                                                            <div className="space-y-2">
                                                                <Label className="text-xs">Field Name</Label>
                                                                <Input
                                                                    value={field.field}
                                                                    onChange={(e) => {
                                                                        const value = toSnakeCase(e.target.value);
                                                                        updateField(field.airtableId, { field: value });
                                                                    }}
                                                                    className={cn(
                                                                        'font-mono text-sm',
                                                                        !nameValid && 'border-destructive'
                                                                    )}
                                                                />
                                                                {!nameValid && (
                                                                    <p className="text-xs text-destructive">
                                                                        Invalid field name
                                                                    </p>
                                                                )}
                                                            </div>
                                                            <div className="space-y-2">
                                                                <Label className="text-xs">Type</Label>
                                                                <Select
                                                                    value={field.type}
                                                                    onValueChange={(value) =>
                                                                        updateField(field.airtableId, { type: value })
                                                                    }
                                                                >
                                                                    <SelectTrigger className="text-sm">
                                                                        <SelectValue />
                                                                    </SelectTrigger>
                                                                    <SelectContent>
                                                                        {FIELD_TYPES.map((type) => (
                                                                            <SelectItem key={type.value} value={type.value}>
                                                                                {type.label}
                                                                            </SelectItem>
                                                                        ))}
                                                                    </SelectContent>
                                                                </Select>
                                                            </div>
                                                            <div className="space-y-2">
                                                                <Label className="text-xs">Interface</Label>
                                                                <Select
                                                                    value={field.interface}
                                                                    onValueChange={(value) =>
                                                                        updateField(field.airtableId, { interface: value })
                                                                    }
                                                                >
                                                                    <SelectTrigger className="text-sm">
                                                                        <SelectValue />
                                                                    </SelectTrigger>
                                                                    <SelectContent>
                                                                        {INTERFACES.map((iface) => (
                                                                            <SelectItem key={iface.value} value={iface.value}>
                                                                                {iface.label}
                                                                            </SelectItem>
                                                                        ))}
                                                                    </SelectContent>
                                                                </Select>
                                                            </div>
                                                        </div>

                                                        {/* Editable Choices - Only show for interfaces that support choices */}
                                                        {(['select-dropdown', 'select-multiple-dropdown', 'select-radio'].includes(field.interface) && isEditing) && (
                                                            <div className="pt-3 border-t space-y-2">
                                                                <div className="flex items-center justify-between">
                                                                    <Label className="text-xs">Choices</Label>
                                                                    <Button
                                                                        variant="outline"
                                                                        size="sm"
                                                                        onClick={() => addChoice(field.airtableId)}
                                                                        className="h-7 text-xs"
                                                                    >
                                                                        <Plus className="h-3 w-3 mr-1" />
                                                                        Add Choice
                                                                    </Button>
                                                                </div>
                                                                <div className="space-y-2 max-h-48 overflow-y-auto">
                                                                    {((field.options?.choices as { text: string; value: string }[] | undefined) || []).map((choice, idx) => (
                                                                        <div key={idx} className="flex gap-2">
                                                                            <Input
                                                                                value={choice.text}
                                                                                onChange={(e) =>
                                                                                    updateChoice(field.airtableId, idx, 'text', e.target.value)
                                                                                }
                                                                                placeholder="Display text"
                                                                                className="flex-1 text-sm"
                                                                            />
                                                                            <Input
                                                                                value={choice.value}
                                                                                onChange={(e) =>
                                                                                    updateChoice(field.airtableId, idx, 'value', e.target.value)
                                                                                }
                                                                                placeholder="Value"
                                                                                className="flex-1 text-sm font-mono"
                                                                            />
                                                                            <Button
                                                                                variant="ghost"
                                                                                size="icon"
                                                                                onClick={() => removeChoice(field.airtableId, idx)}
                                                                                className="h-9 w-9 shrink-0"
                                                                            >
                                                                                <Trash2 className="h-4 w-4" />
                                                                            </Button>
                                                                        </div>
                                                                    ))}
                                                                    {(!field.options?.choices || (field.options.choices as { text: string; value: string }[]).length === 0) && (
                                                                        <p className="text-xs text-muted-foreground text-center py-2">
                                                                            No choices defined. Click "Add Choice" to add options.
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div className="space-y-3">
                                                        <div className="flex items-center gap-4 text-sm">
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-muted-foreground">Name:</span>
                                                                <code className={cn(
                                                                    'bg-muted px-2 py-0.5 rounded font-mono',
                                                                    !nameValid && 'bg-destructive/10 text-destructive'
                                                                )}>
                                                                    {field.field}
                                                                </code>
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-muted-foreground">Type:</span>
                                                                <Badge variant="secondary">{field.type}</Badge>
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-muted-foreground">Interface:</span>
                                                                <Badge variant="outline">{field.interface}</Badge>
                                                            </div>
                                                        </div>

                                                        {/* Show choices if present (read-only view) */}
                                                        {(() => {
                                                            const choices = field.options?.choices as { text: string; value: string }[] | undefined;
                                                            return choices && choices.length > 0 ? (
                                                                <div className="pt-2 border-t">
                                                                    <p className="text-xs text-muted-foreground mb-2">Choices:</p>
                                                                    <div className="flex flex-wrap gap-1">
                                                                        {choices.slice(0, 10).map((choice, idx) => (
                                                                            <Badge key={idx} variant="outline" className="text-xs">
                                                                                {choice.text}
                                                                                {choice.text !== choice.value && (
                                                                                    <span className="ml-1 text-muted-foreground font-mono">
                                                                                        ({choice.value})
                                                                                    </span>
                                                                                )}
                                                                            </Badge>
                                                                        ))}
                                                                        {choices.length > 10 && (
                                                                            <Badge variant="outline" className="text-xs">
                                                                                +{choices.length - 10} more
                                                                            </Badge>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            ) : null;
                                                        })()}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </ScrollArea>
                        </div>
                    )}
                </div>

                {/* Footer Actions */}
                <div className="flex-shrink-0 flex justify-between gap-2 border-t border-border bg-background p-4">
                    {step === 'credentials' ? (
                        <>
                            <div />
                            <div className="flex gap-2">
                                <Button variant="outline" onClick={handleClose}>
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleFetchTables}
                                    disabled={!baseId.trim() || !apiToken.trim() || isLoadingTables}
                                >
                                    {isLoadingTables ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Connecting...
                                        </>
                                    ) : (
                                        <>
                                            <ArrowRight className="mr-2 h-4 w-4" />
                                            Connect
                                        </>
                                    )}
                                </Button>
                            </div>
                        </>
                    ) : step === 'table' ? (
                        <>
                            <Button variant="outline" onClick={handleBack}>
                                <ArrowLeft className="mr-2 h-4 w-4" />
                                Back
                            </Button>
                            <div className="flex gap-2">
                                <Button variant="outline" onClick={handleClose}>
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleContinueToFields}
                                    disabled={!selectedTableId}
                                >
                                    <ArrowRight className="mr-2 h-4 w-4" />
                                    Continue
                                </Button>
                            </div>
                        </>
                    ) : step === 'fields' ? (
                        <>
                            <Button variant="outline" onClick={handleBack}>
                                <ArrowLeft className="mr-2 h-4 w-4" />
                                Back
                            </Button>
                            <div className="flex gap-2">
                                <Button variant="outline" onClick={handleClose}>
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleContinueToPreview}
                                    disabled={selectedFields.length === 0}
                                >
                                    <ArrowRight className="mr-2 h-4 w-4" />
                                    Preview ({selectedFields.length})
                                </Button>
                            </div>
                        </>
                    ) : (
                        <>
                            <Button variant="outline" onClick={handleBack}>
                                <ArrowLeft className="mr-2 h-4 w-4" />
                                Back
                            </Button>
                            <div className="flex gap-2">
                                <Button variant="outline" onClick={handleClose}>
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleImport}
                                    disabled={selectedFields.length === 0 || isImporting || hasDuplicateNames || !allNamesValid}
                                >
                                    {isImporting ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Importing...
                                        </>
                                    ) : (
                                        <>
                                            <Check className="mr-2 h-4 w-4" />
                                            Import {selectedFields.length} Field{selectedFields.length !== 1 ? 's' : ''}
                                        </>
                                    )}
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </SheetContent>
        </Sheet>
    );
}

